(function () {
  'use strict';

  /**
   * tl-route-autoadd.js  (v6 — Route lives at checkout only, unchecked)
   *
   * The filename is historical. This file NO LONGER ADDS Route. It does the
   * opposite. Kept under the same name so layout/theme.liquid did not need to
   * change.
   *
   * DECISION (Med, Sept 16 2026): Route Package Protection comes out of the
   * cart drawer entirely. It is offered only at checkout, UNCHECKED, and the
   * customer opts in there if they want it.
   *
   * WHY THIS FILE STILL HAS A JOB. Route's checkout block checks itself by
   * default. Verified Sept 16 2026 on live checkouts:
   *   - cart with no Route line and no route_toggle_state attribute
   *       -> checkout ADDS Route and shows it checked ($4.15 on two tees)
   *   - same cart with route_toggle_state = 0
   *       -> checkout shows "Add Shipping Protection by Route for $4.15", unchecked
   *   - ticking the box at checkout writes route_toggle_state = "1" and adds
   *     the Route line to the cart
   * So "unchecked at checkout" depends on that cart attribute being 0.
   *
   * WHAT THIS FILE DOES, on every page load, on every cart:change, and when a
   * page is restored from the back/forward cache (a shopper coming Back from
   * checkout):
   *   1. sets the cart attribute route_toggle_state to 0 if it isn't already;
   *   2. removes any Route line from the cart.
   * Both in one /cart/update.js call, and only when something is actually
   * wrong, so a normal page view costs a single /cart.js read.
   *
   * Consequence, on purpose: a shopper who ticks Route at checkout and then
   * comes back to the store starts unchecked again next time. Every visit to
   * checkout begins with the drawer total and the checkout total matching.
   * That mismatch is exactly what customers complained about.
   *
   * The Route line is removed rather than hidden so the drawer total, the
   * header count and the cart page never carry a Route amount. Liquid also
   * ignores any Route line for the moment before this runs (see
   * snippets/tl-route-quote.liquid).
   *
   * If Route is ever put back in the drawer, the v5 file (true Route pricing,
   * sections-aware re-render) is in the Sept 16 project notes.
   */

  var ROUTE_HANDLE = 'routeins';
  var ATTR = 'route_toggle_state';

  var _busy = false;
  var _again = false;
  var _timer = null;

  function isRoute(item) {
    return item.handle === ROUTE_HANDLE;
  }

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  // Prestige's <cart-drawer> re-renders from event.detail.cart.sections, so a
  // cart:change without `sections` is silently ignored. Collect the section ids
  // the page wants (the same hook Prestige's own add-to-cart uses), render them,
  // and attach them before dispatching.
  function bundledSectionIds() {
    var ev = new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: [] } });
    document.documentElement.dispatchEvent(ev);
    return ev.detail.sections.filter(function (id, i, all) { return id && all.indexOf(id) === i; });
  }

  async function refreshCartUI() {
    // The cart page is server-rendered; just reload it. Only reached when a
    // Route line was actually removed, so this cannot loop.
    if (isCartPage()) { window.location.reload(); return; }
    try {
      var ids = bundledSectionIds();
      var sectionsPromise = ids.length
        ? fetch(rootUrl() + '?sections=' + encodeURIComponent(ids.join(','))).then(function (r) { return r.json(); })
        : Promise.resolve({});
      var cart = await fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      cart.sections = await sectionsPromise;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { cart: cart } })
      );
    } catch (err) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  async function enforce() {
    if (_busy) { _again = true; return; }
    _busy = true;
    var removed = false;
    try {
      var cart = await fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      if (!cart.item_count) return;

      var routeLines = cart.items.filter(isRoute);
      var attrs = cart.attributes || {};
      var attrOk = String(attrs[ATTR]) === '0';
      if (!routeLines.length && attrOk) return;

      var body = { attributes: {} };
      body.attributes[ATTR] = 0;
      if (routeLines.length) {
        body.updates = {};
        routeLines.forEach(function (line) { body.updates[line.key] = 0; });
      }

      await fetch(rootUrl() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      removed = routeLines.length > 0;
    } catch (err) {
      // Non-critical. The next page view or cart change tries again.
    } finally {
      _busy = false;
      if (removed) await refreshCartUI();
      if (_again) { _again = false; schedule(); }
    }
  }

  function schedule() {
    clearTimeout(_timer);
    _timer = setTimeout(enforce, 300);
  }

  // Our own refreshCartUI dispatch also lands here. That second pass finds
  // nothing to fix and stops after one read.
  document.addEventListener('cart:change', schedule);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) schedule();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
